/**
 * A calendar day, in a named timezone, as the two instants it runs between.
 *
 * The picker draws every day midnight to midnight in the MEETING's zone — not
 * the browser's, so a card reviewed from another country still shows the hours
 * the meeting will actually happen at. Positions on the grid are proportions of
 * that span, so both ends are needed rather than a start plus 24 hours: on a
 * clock-change day the two midnights are 23 or 25 hours apart, and assuming 24
 * puts everything after the change in the wrong place.
 *
 * Computed on the client so paging to a day never waits on a request — the grid
 * can draw any date, any distance out, before a single byte arrives.
 *
 * Shared by web and native, which draw the same day from the same wire data.
 */

/** How far the named zone is from UTC at a given instant, in milliseconds. */
function zoneOffsetMs(atMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(atMs));

  const read = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  // Reading the wall clock back as if it were UTC gives the offset directly.
  // `hour` comes through as 24 at midnight in some engines; 24 % 24 is 0.
  const asUtc = Date.UTC(
    read("year"),
    read("month") - 1,
    read("day"),
    read("hour") % 24,
    read("minute"),
    read("second"),
  );
  return asUtc - atMs;
}

/** The instant midnight falls on, for `YYYY-MM-DD` in the named zone. */
export function zonedMidnightMs(date: string, timeZone: string): number {
  const [year, month, day] = date.split("-").map(Number);
  const midnightAsUtc = Date.UTC(year, month - 1, day);

  // Correct the UTC guess by the zone's offset, then check the offset again at
  // the corrected instant: near a clock change the two can differ, and the
  // second reading is the one that belongs to the day being asked for.
  const firstGuess = midnightAsUtc - zoneOffsetMs(midnightAsUtc, timeZone);
  const settled = midnightAsUtc - zoneOffsetMs(firstGuess, timeZone);
  return settled;
}

/** Midnight to midnight for `YYYY-MM-DD` in the named zone. */
export function dayBoundsIn(
  date: string,
  timeZone: string,
): { startMsSinceEpoch: number; endMsSinceEpoch: number } {
  return {
    startMsSinceEpoch: zonedMidnightMs(date, timeZone),
    endMsSinceEpoch: zonedMidnightMs(addDays(date, 1), timeZone),
  };
}

/** `YYYY-MM-DD` shifted by whole days, without falling off the calendar. */
export function addDays(date: string, delta: number): string {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + delta)).toISOString().slice(0, 10);
}

/**
 * The `YYYY-MM-DD` an instant falls on, in the named zone.
 *
 * Assembled from parts rather than read off an `en-CA` format string. A locale's
 * pattern is the engine's to choose, and React Native runs on Hermes, whose Intl
 * is the platform's rather than V8's — so the shape of a formatted date is not
 * something both callers of this file can assume.
 */
export function dateOf(atMs: number, timeZone: string): string {
  const parts = dateFormatter(timeZone).formatToParts(new Date(atMs));

  const read = (type: string) => {
    const value = parts.find((p) => p.type === type)?.value;
    // A date this malformed is an engine fault, not a user condition, and every
    // caller compares these strings — `>`, `<`, `===`, a query key, the day the
    // grid draws. A short one silently means a different day, so it throws here
    // instead of travelling.
    if (!value) throw new Error(`dateOf: no ${type} part in ${timeZone}`);
    return value;
  };
  // Padded rather than trusted to `2-digit`: the comparisons above are
  // lexicographic, so a single-digit month sorts wrong against a padded one.
  const pad = (value: string) => value.padStart(2, "0");
  return `${read("year")}-${pad(read("month"))}-${pad(read("day"))}`;
}

/** Held per zone — both clients call this on every render. */
const dateFormatters = new Map<string, Intl.DateTimeFormat>();
function dateFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = dateFormatters.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  dateFormatters.set(timeZone, formatter);
  return formatter;
}

/** Today's `YYYY-MM-DD` in the meeting's zone, not the device's. */
export function todayIn(timeZone: string): string {
  return dateOf(Date.now(), timeZone);
}
